/**
 * SubjectChipPicker — chips input with autocomplete over the curated
 * subject taxonomy (`GET /api/subjects/suggest`).
 *
 * Why this exists: the Student Profile "What do you want to study?"
 * field used to be a raw free-text chips input. Students typed informal
 * labels like "Movement" or "Screenwriting" that don't resolve to any
 * canonical in the Fast Match taxonomy, which then hard-gated Advanced
 * Search to zero results. This picker funnels users toward canonical
 * labels while still allowing free text (we also now soft-fail on
 * unknown terms on the backend so a stray chip can't nuke the whole
 * match pool).
 *
 * Props
 * -----
 *   value       — string[] of chip labels (profile is persisted as the
 *                 user-facing string per chip, e.g. "Dance").
 *   onChange    — called with the new string[] whenever chips change.
 *   placeholder — input placeholder.
 *   testid      — root test id (chips get `${testid}-chips`, input
 *                 gets `${testid}`, suggestions get
 *                 `${testid}-suggestions`).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

export default function SubjectChipPicker({ value, onChange, placeholder, testid }) {
  const chips = Array.isArray(value)
    ? value
    : (typeof value === "string" && value.trim()) ? [value.trim()] : [];

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get(`/subjects/suggest?q=${encodeURIComponent(query)}&limit=8`)
        .then(({ data }) => {
          const suggestions = Array.isArray(data) ? data : (data?.items || []);
          // Hide canonicals the user has already picked.
          const picked = new Set(chips.map((c) => c.toLowerCase()));
          setItems(suggestions.filter((s) => {
            const label = (s.canonical || s.label || "").toLowerCase();
            return label && !picked.has(label);
          }));
          setActiveIdx(0);
        })
        .catch(() => setItems([]));
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const addChip = (raw) => {
    const label = (raw || "").trim();
    if (!label) return;
    const exists = chips.some((c) => c.toLowerCase() === label.toLowerCase());
    if (exists) {
      setQuery("");
      return;
    }
    onChange([...chips, label]);
    setQuery("");
  };

  const removeChip = (idx) => {
    onChange(chips.filter((_, i) => i !== idx));
  };

  const prettyLabel = (it) => {
    const raw = it.canonical || it.label || "";
    // The /subjects/suggest endpoint returns canonicals in lowercase
    // (e.g. "computer science"). Title-case them so the picked chip
    // looks right on the ticket and in the profile summary.
    return raw.replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const showSuggestions = open && items.length > 0;

  return (
    <div className="relative" ref={containerRef} data-testid={`${testid}-root`}>
      {chips.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5 mb-1.5"
          data-testid={`${testid}-chips`}
        >
          {chips.map((c, i) => (
            <span
              key={`${c}-${i}`}
              className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[12px] px-2 py-0.5 rounded-full"
            >
              {c}
              <button
                type="button"
                onClick={() => removeChip(i)}
                className="text-emerald-600 hover:text-emerald-900"
                aria-label={`Remove ${c}`}
                data-testid={`${testid}-chip-${i}-remove`}
              >×</button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        data-testid={testid}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (showSuggestions) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIdx((i) => Math.min(i + 1, items.length - 1));
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIdx((i) => Math.max(i - 1, 0));
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              const pick = items[activeIdx] || items[0];
              addChip(prettyLabel(pick));
              return;
            }
            if (e.key === "Escape") { setOpen(false); return; }
          }
          if ((e.key === "Enter" || e.key === ",") && query.trim()) {
            e.preventDefault();
            addChip(query.trim());
          }
          if (e.key === "Backspace" && !query && chips.length > 0) {
            removeChip(chips.length - 1);
          }
        }}
        className="w-full border border-stone-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
      />
      {showSuggestions && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-72 overflow-auto bg-white border border-stone-200 rounded-md shadow-lg py-1"
          data-testid={`${testid}-suggestions`}
        >
          {items.map((it, i) => {
            const label = prettyLabel(it);
            const aliases = Array.isArray(it.aliases) ? it.aliases : [];
            return (
              <li
                key={`${it.canonical || it.label}-${i}`}
                role="option"
                aria-selected={i === activeIdx}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={(e) => { e.preventDefault(); addChip(label); }}
                className={`px-3 py-2 cursor-pointer text-[13px] ${
                  i === activeIdx ? "bg-[#F5F3FF] text-[#4C1D95]" : "text-stone-800"
                }`}
                data-testid={`${testid}-suggestion-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">{label}</span>
                  {typeof it.count === "number" && it.count > 0 && (
                    <span className="text-[11px] text-stone-500 font-mono">
                      {it.count} degrees
                    </span>
                  )}
                </div>
                {aliases.length > 0 && (
                  <div className="text-[11px] text-stone-500 truncate">
                    Also: {aliases.slice(0, 4).join(" · ")}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
